import { DEFAULT_COUNTRY } from '../constants/countries';
import { BILLING_FIELD_NAMES } from '../constants/billingFields';
import { US_STATE_CODES, US_STATE_OPTIONS } from '../constants/usStates';

const REQUIRED_BILLING_FIELDS = [
  'invoiceCompanyName',
  'billingContactName',
  'billingEmail',
  'addressLine1',
  'city',
  'state',
  'postalCode',
  'country',
];

export function isValidEmail(value) {
  if (!value || typeof value !== 'string') {
    return false;
  }
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function parseBillingEmails(value) {
  if (!value || typeof value !== 'string') {
    return [];
  }
  return value
    .split(',')
    .map((email) => email.trim())
    .filter(Boolean);
}

export function normalizeBillingEmailList(value) {
  return parseBillingEmails(value).join(', ');
}

export function isValidBillingEmailList(value) {
  const emails = parseBillingEmails(value);
  return emails.length > 0 && emails.every((email) => isValidEmail(email));
}

export function validateBillingEmail(value) {
  if (!value || !String(value).trim()) {
    return 'Email is required';
  }
  const emails = parseBillingEmails(value);
  if (!emails.length) {
    return 'Email is required';
  }
  const invalidEmail = emails.find((email) => !isValidEmail(email));
  if (invalidEmail) {
    return `Invalid email: ${invalidEmail}`;
  }
  return true;
}

export function isBillingDetailsComplete(billingDetails) {
  if (!billingDetails) {
    return false;
  }
  const hasRequiredFields = REQUIRED_BILLING_FIELDS.every((field) => {
    if (field === 'billingEmail') {
      return isValidBillingEmailList(billingDetails[field]);
    }
    const value = billingDetails[field];
    return typeof value === 'string' && value.trim().length > 0;
  });
  return hasRequiredFields;
}

export function getMissingBillingFields(billingDetails) {
  if (!billingDetails) {
    return [...REQUIRED_BILLING_FIELDS];
  }
  return REQUIRED_BILLING_FIELDS.filter((field) => {
    if (field === 'billingEmail') {
      return !isValidBillingEmailList(billingDetails[field]);
    }
    const value = billingDetails[field];
    return !value || !String(value).trim();
  });
}

function billingEmailsFromList(value) {
  return parseBillingEmails(value).filter((email) => isValidEmail(email));
}

export function normalizeBillingDetails(values = {}, existing = {}) {
  const merged = { ...existing, ...values };

  return BILLING_FIELD_NAMES.reduce((acc, field) => {
    const value = merged[field];
    if (field === 'billingEmail') {
      acc[field] = normalizeBillingEmailList(typeof value === 'string' ? value : '');
      return acc;
    }
    if (typeof value === 'string') {
      acc[field] = value.trim();
    } else if (value == null) {
      acc[field] = '';
    } else {
      acc[field] = String(value).trim();
    }
    return acc;
  }, {});
}

export function mergeBillingDetails({
  businessInfo,
  companyName,
  storedBilling,
  customerUsers,
  allowedEmails,
}) {
  const stored = storedBilling || {};
  const userEmail = customerUsers?.find((user) => isValidEmail(user?.email))?.email?.trim();

  return normalizeBillingDetails(
    {
      invoiceCompanyName:
        stored.invoiceCompanyName?.trim() ||
        businessInfo?.legalName?.trim() ||
        companyName?.trim() ||
        '',
      billingContactName:
        stored.billingContactName?.trim() || businessInfo?.founders?.trim() || '',
      billingContactTitle: stored.billingContactTitle?.trim() || '',
      billingEmail:
        stored.billingEmail?.trim() ||
        userEmail ||
        billingEmailsFromList(allowedEmails).join(', ') ||
        billingEmailsFromList(businessInfo?.allowedEmails).join(', ') ||
        '',
      addressLine1: stored.addressLine1?.trim() || '',
      addressLine2: stored.addressLine2?.trim() || '',
      city: stored.city?.trim() || '',
      state: stored.state?.trim() || '',
      postalCode: stored.postalCode?.trim() || '',
      country: stored.country?.trim() || DEFAULT_COUNTRY,
    },
    stored
  );
}

const US_STATE_NAME_TO_CODE = US_STATE_OPTIONS.reduce((lookup, option) => {
  const stateName = option.label.split('—')[1]?.trim().toLowerCase();
  if (stateName) {
    lookup[stateName] = option.value;
  }
  return lookup;
}, {});

export function formatBillingStateAbbreviation(state) {
  if (!state) {
    return '';
  }

  const trimmed = String(state).trim();
  const upper = trimmed.toUpperCase();

  if (US_STATE_CODES.has(upper)) {
    return upper;
  }

  const matchedCode = US_STATE_NAME_TO_CODE[trimmed.toLowerCase()];
  if (matchedCode) {
    return matchedCode;
  }

  if (trimmed.length <= 2) {
    return upper;
  }

  return upper.slice(0, 2);
}

export function formatBillingContactAttention(billingDetails) {
  const name = billingDetails?.billingContactName?.trim();
  const title = billingDetails?.billingContactTitle?.trim();

  if (!name) {
    return '';
  }

  return title ? `${name}, ${title}` : name;
}

export function formatBillingAddressLines(billingDetails) {
  const lines = [];
  if (billingDetails?.addressLine1) {
    lines.push(billingDetails.addressLine1);
  }
  if (billingDetails?.addressLine2) {
    lines.push(billingDetails.addressLine2);
  }
  const cityLine = [
    billingDetails?.city,
    formatBillingStateAbbreviation(billingDetails?.state),
    billingDetails?.postalCode,
  ]
    .filter(Boolean)
    .join(', ');
  if (cityLine) {
    lines.push(cityLine);
  }
  if (billingDetails?.country) {
    lines.push(billingDetails.country);
  }
  return lines;
}
